import { createAxiosClient, handleError } from "@aladin/shared-common-sdk";
import { AladinError, isAladinErrorData } from "@aladin/shared-error";
import { NOOP_LOGGER } from "@aladin/shared-logger";
import { ShoppingListApiErrorType, } from "./types";
import { transformResponse } from "./transform-response";
export function createShoppingListApiClient(options) {
    const logger = options.logger ?? NOOP_LOGGER;
    const client = createAxiosClient(options.shoppingListApiHost, {
        accessTokenSource: options.accessTokenSource,
    });
    const basePathV1 = options.useCartV2 ? "/v1/bff/appro" : "/v1";
    const basePathV2 = options.useCartV2 ? "/v1/bff/appro" : "/v2";
    function getQueryParams(filters, paginationParameters) {
        const queryParams = new URLSearchParams();
        if (filters?.membershipNumbers && filters?.membershipNumbers.length > 0)
            queryParams.append("membership_number", filters?.membershipNumbers.toString());
        if (filters?.status)
            queryParams.append("status", filters.status);
        if (paginationParameters?.page)
            queryParams.append("page", paginationParameters.page.toString());
        if (paginationParameters?.limit)
            queryParams.append("limit", paginationParameters.limit.toString());
        return queryParams;
    }
    async function fetchOwnedShoppingLists(filters, paginationParameters) {
        try {
            const response = await client.get(`${basePathV1}/shopping-lists`, {
                params: getQueryParams(filters, paginationParameters),
                transformResponse,
            });
            return response.data;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            logger.debug("%s", err.stack);
            logger.debug("%s", err.message);
            handleError(err);
        }
    }
    async function fetchOwnedShoppingListsMetadata(filters) {
        try {
            const response = await client.get(`${basePathV1}/shopping-lists/meta`, {
                params: getQueryParams(filters),
                transformResponse,
            });
            return response.data;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            logger.debug("%s", err.stack);
            logger.debug("%s", err.message);
            handleError(err);
        }
    }
    async function fetchOwnedShoppingList(shoppingListId) {
        try {
            const response = await client.get(`${basePathV1}/shopping-lists/${shoppingListId}`, {
                transformResponse,
            });
            return response.data;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            const error = err.response?.data;
            if (isAladinErrorData(error, ShoppingListApiErrorType.SHOPPING_LIST_NOT_FOUND_ERROR)) {
                return null;
            }
            logger.debug("%s", err.stack);
            logger.debug("%s", err.message);
            handleError(err);
        }
    }
    async function saveShoppingList(shoppingListApi) {
        try {
            const response = await client.post(`${basePathV1}/shopping-lists`, shoppingListApi, {
                transformResponse,
            });
            return response.data;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            logger.debug("%s", err.stack);
            logger.debug("%s", err.message);
            handleError(err);
        }
    }
    async function addLineToShoppingList(id, line) {
        // TODO: Improve this later if needed.
        // The complete shopping list is fetched each time a line is added for quick development. For later optimization
        // it should be possible to do the list update server-side if there are performance concerns.
        const shoppingList = await fetchOwnedShoppingList(id);
        if (shoppingList === null) {
            throw new AladinError({
                key: ShoppingListApiErrorType.SHOPPING_LIST_NOT_FOUND_ERROR,
                message: `Could not find shopping list with id ${id}`,
                details: {
                    id,
                },
            });
        }
        updateShoppingListLine(shoppingList, line);
        await saveShoppingList(shoppingList);
    }
    async function checkoutV2ShoppingList(shoppingListId) {
        try {
            const response = await client.post(`${basePathV2}/shopping-lists/${shoppingListId}/checkout`, undefined, {
                transformResponse,
            });
            return response.data;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            logger.debug("%s", err.stack);
            logger.debug("%s", err.message);
            handleError(err);
        }
    }
    async function sendShoppingList(shoppingListId) {
        try {
            const response = await client.post(`${basePathV1}/shopping-lists/${shoppingListId}/send`, undefined, { transformResponse });
            logger.debug("%s", response.data);
            return response.data;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            logger.debug("%s", err.stack);
            logger.debug("%s", err.message);
            handleError(err);
        }
    }
    async function flushShoppingListValidationErrors(shoppingListId) {
        try {
            const response = await client.post(`${basePathV1}/shopping-lists/${shoppingListId}/flush-errors`, undefined, {
                transformResponse,
            });
            return response.data;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            const error = err.response?.data;
            if (isAladinErrorData(error, ShoppingListApiErrorType.SHOPPING_LIST_NOT_FOUND_ERROR)) {
                return null;
            }
            logger.debug("%s", err.stack);
            logger.debug("%s", err.message);
            handleError(err);
        }
    }
    async function confirmShoppingListCheckout(shoppingListId, orderIds) {
        try {
            await client.post(`${basePathV1}/shopping-lists/${shoppingListId}/confirm-checkout`, {
                order_ids: orderIds,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            logger.debug("%s", err.stack);
            logger.debug("%s", err.message);
            handleError(err);
        }
    }
    async function cancelShoppingListCheckout(shoppingListId) {
        try {
            await client.post(`${basePathV1}/shopping-lists/${shoppingListId}/cancel-checkout`, undefined);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            logger.debug("%s", err.stack);
            logger.debug("%s", err.message);
            handleError(err);
        }
    }
    async function removeShoppingList(shoppingListId) {
        try {
            await client.delete(`${basePathV1}/shopping-lists/${shoppingListId}`, undefined);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            logger.debug("%s", err.stack);
            logger.debug("%s", err.message);
            handleError(err);
        }
    }
    async function duplicateShoppingList(shoppingListId, shoppingListApi) {
        try {
            const response = await client.post(`${basePathV1}/shopping-lists/${shoppingListId}/duplicate`, shoppingListApi, {
                transformResponse,
            });
            return response.data;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (err) {
            logger.debug("%s", err.stack);
            logger.debug("%s", err.message);
            handleError(err);
        }
    }
    return {
        fetchOwnedShoppingListsMetadata,
        fetchOwnedShoppingLists,
        fetchOwnedShoppingList,
        saveShoppingList,
        addLineToShoppingList,
        sendShoppingList,
        checkoutV2ShoppingList,
        flushShoppingListValidationErrors,
        confirmShoppingListCheckout,
        cancelShoppingListCheckout,
        removeShoppingList,
        duplicateShoppingList,
    };
}
function updateShoppingListLine(shoppingList, line) {
    let currentLine = shoppingList.lines.find((elem) => elem.offer.id === line.offer.id);
    if (currentLine !== undefined) {
        updateExistingLine(currentLine, line);
    }
    else {
        shoppingList.lines.push(line);
    }
}
function updateExistingLine(oldLine, newLine) {
    oldLine.quantity += newLine.quantity;
    if (newLine.payment_due_date) {
        oldLine.payment_due_date = newLine.payment_due_date;
    }
    else {
        delete oldLine.payment_due_date;
    }
    if (newLine.services) {
        oldLine.services = newLine.services;
    }
    else {
        delete oldLine.services;
    }
}
//# sourceMappingURL=shopping-list.client.js.map